"use client";

import React from "react";
import { motion } from "framer-motion";
import { ArrowUpRight, CircleCheck as CheckCircle2, Globe, Layers, Link2, TriangleAlert as AlertTriangle, Server, Network } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
export default function HreflangInternationalSeo() {
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.12,
        delayChildren: 0.1
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: 24
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };
  return <section id="hreflanginternationalseo" data-nav="light" className="relative bg-background text-foreground py-20 lg:py-28 overflow-hidden">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: "-80px"
      }} variants={containerVariants} className="space-y-16">
          {/* Header Block */}
          <div className="max-w-3xl space-y-5">
            <motion.div variants={itemVariants}>
              <Badge variant="secondary" className="px-3 py-1 font-mono uppercase tracking-wider text-xs">
                INTERNATIONAL ARCHITECTURE
              </Badge>
            </motion.div>

            <motion.h2 variants={itemVariants} className="text-3xl sm:text-4xl lg:text-5xl font-serif font-bold text-foreground tracking-tight">
              Hreflang and International Technical SEO
            </motion.h2>

            <motion.p variants={itemVariants} className="text-base sm:text-lg text-muted-foreground leading-relaxed">
              Hreflang annotations inform search engines which language and regional alternatives of a page exist, but they
              do not guarantee which URL will be served in search results. Search engines evaluate user location, device
              settings, search query intent, and localized signals alongside hreflang markup. As part of my broader{" "}
              <Link to="/TechnicalSeoPage" className="font-medium text-primary underline underline-offset-4 hover:text-primary/80 inline-flex items-center gap-0.5">
                Technical SEO
                <ArrowUpRight className="size-3.5" />
              </Link>{" "}
              work, structuring clean technical tags ensures crawlers can interpret and index your multi-regional content
              without confusion.
            </motion.p>
          </div>

          <Separator className="bg-border" />

          {/* Canonical vs Hreflang Comparison Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-stretch">
            <motion.div variants={itemVariants} className="h-full">
              <Card className="h-full border border-border bg-card shadow-xs hover:border-primary/30 transition-colors">
                <CardHeader className="pb-4">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="p-2 rounded-md bg-secondary text-foreground">
                      <Link2 className="size-5 text-primary" />
                    </div>
                    <span className="font-mono text-xs uppercase tracking-wider text-muted-foreground">
                      Purpose: Indexing & Preferred URL
                    </span>
                  </div>
                  <CardTitle className="text-xl font-bold font-serif">
                    <h3 className="text-xl font-bold font-serif">Canonical Tags</h3>
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4 text-sm sm:text-base text-muted-foreground leading-relaxed">
                  <p>
                    Canonical tags identify the preferred URL among duplicates or near-duplicates. In an international
                    architecture, canonical tags and hreflang annotations serve distinct jobs and must remain consistent.
                  </p>
                  <div className="p-4 rounded-md bg-muted/60 border border-border/60 text-xs font-mono text-foreground space-y-1">
                    <span className="text-muted-foreground">// Rule: Each localised page normally self-canonicalises</span>
                    <p className="break-all">{`<link rel="canonical" href="https://yoursite.com/fr/" />`}</p>
                  </div>
                  <p>
                    A common mistake is pointing a localised regional page’s canonical tag back to the UK or default
                    version. Canonicalising a localised page to another language version directly contradicts its hreflang
                    markup and can lead search engines to ignore the localised URL entirely.
                  </p>
                </CardContent>
              </Card>
            </motion.div>

            <motion.div variants={itemVariants} className="h-full">
              <Card className="h-full border border-border bg-card shadow-xs hover:border-primary/30 transition-colors">
                <CardHeader className="pb-4">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="p-2 rounded-md bg-secondary text-foreground">
                      <Globe className="size-5 text-primary" />
                    </div>
                    <span className="font-mono text-xs uppercase tracking-wider text-muted-foreground">
                      Purpose: Alternate Language Routing
                    </span>
                  </div>
                  <CardTitle className="text-xl font-bold font-serif">
                    <h3 className="text-xl font-bold font-serif">Hreflang Annotations</h3>
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4 text-sm sm:text-base text-muted-foreground leading-relaxed">
                  <p>
                    Hreflang tags declare alternate language and regional versions for different audiences. They point
                    exclusively to canonical URLs and can be deployed in the HTML head, HTTP headers, or XML sitemaps.
                  </p>
                  <div className="p-4 rounded-md bg-muted/60 border border-border/60 text-xs font-mono text-foreground space-y-1">
                    <span className="text-muted-foreground">// Rule: Reciprocal mappings across target regions</span>
                    <p className="break-all">{`<link rel="alternate" hreflang="fr-fr" href="https://yoursite.com/fr/" />`}</p>
                  </div>
                  <p>
                    Hreflang requires valid ISO language and regional codes, reciprocal return links on all alternate
                    pages, and an <code className="text-xs bg-muted px-1 py-0.5 rounded font-mono">x-default</code> tag
                    for an international selector or default fallback.
                  </p>
                </CardContent>
              </Card>
            </motion.div>
          </div>

          {/* Essential Implementation Requirements Card */}
          <motion.div variants={itemVariants}>
            <Card className="border border-border bg-muted/30 shadow-xs">
              <CardHeader className="pb-2">
                <div className="flex items-center gap-2">
                  <Layers className="size-5 text-primary" />
                  <CardTitle className="text-xl font-serif font-bold">
                    <h3 className="text-xl font-serif font-bold">
                      Core Technical Requirements for International Deployments
                    </h3>
                  </CardTitle>
                </div>
              </CardHeader>
              <CardContent className="space-y-6 pt-2">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 font-medium text-foreground">
                      <CheckCircle2 className="size-4 text-chart-4 shrink-0" />
                      <h4 className="text-sm font-semibold text-foreground">Reciprocal Links &amp; Self-Reference</h4>
                    </div>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      Every localised page must link to all alternate versions and include a self-referencing tag. If page
                      A links to page B as an alternate, page B must link back to page A, otherwise search engines may
                      disregard the relationship.
                    </p>
                  </div>

                  <div className="space-y-2">
                    <div className="flex items-center gap-2 font-medium text-foreground">
                      <CheckCircle2 className="size-4 text-chart-4 shrink-0" />
                      <h4 className="text-sm font-semibold text-foreground">The x-default Fallback</h4>
                    </div>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      Deploying an <code className="text-xs bg-muted px-1.5 py-0.5 rounded font-mono">x-default</code> tag
                      designates a fallback destination or country selector for searchers whose language or region is not
                      explicitly covered by a dedicated page.
                    </p>
                  </div>

                  <div className="space-y-2">
                    <div className="flex items-center gap-2 font-medium text-foreground">
                      <AlertTriangle className="size-4 text-accent shrink-0" />
                      <h4 className="text-sm font-semibold text-foreground">Crawlable Language Switchers</h4>
                    </div>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      Language switchers should be crawlable HTML links. Automatic IP-based redirects often prevent search
                      engine crawlers and users from accessing alternate regional versions; a clear manual selector is far
                      more reliable.
                    </p>
                  </div>
                </div>

                <Separator className="bg-border" />

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-2">
                  <div className="flex items-start gap-3">
                    <div className="p-2 rounded-md bg-secondary text-foreground shrink-0 mt-0.5">
                      <Network className="size-4 text-primary" />
                    </div>
                    <div>
                      <h4 className="text-sm font-semibold text-foreground">Per-Version XML Sitemaps</h4>
                      <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
                        Maintaining separate XML sitemaps for each country or language version clarifies discovery and
                        simplifies indexation tracking across individual Search Console properties and segments.
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <div className="p-2 rounded-md bg-secondary text-foreground shrink-0 mt-0.5">
                      <Server className="size-4 text-primary" />
                    </div>
                    <div>
                      <h4 className="text-sm font-semibold text-foreground">CDN &amp; Server Infrastructure</h4>
                      <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
                        CDN and server location decisions should be made based on latency, page speed, and user experience,
                        rather than an assumption that server location serves as a geotargeting signal.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="pt-4 border-t border-border flex flex-col sm:flex-row sm:items-center justify-between gap-4 text-xs text-muted-foreground font-mono">
                  <span>DEPLOYMENT: HTML HEAD, HTTP HEADERS, OR XML SITEMAPS</span>
                  <span>SEARCH CONSOLE: SEGMENT REPORTING BY COUNTRY &amp; DIRECTORY</span>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </motion.div>
      </div>
    </section>;
}